import { useCallback, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Badge, Button, DataTable, EmptyState, PageHeader, SkeletonTable } from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import {
  useAssignAssignment,
  useCompanyAssignments,
  useUnassignAssignment,
} from "@/hooks/use-company-assignments";
import {
  filterAssignments,
  groupAssignments,
  type DispatchRow,
} from "@/components/dispatch/board-grouping";
import { buildDispatchColumns } from "@/components/dispatch/assignment-columns";
import {
  DispatchFilterBar,
  emptyDispatchFilters,
  type DispatchFilterState,
} from "@/components/dispatch/dispatch-filter-bar";
import { AssignDialog } from "@/components/dispatch/assign-dialog";
import { RosterThreadPane } from "@/components/dispatch/roster-thread-pane";
import { unreadAssignmentIds } from "@/components/dispatch/thread-index";
import { useCompanyMessageThreads } from "@/hooks/use-company-message-threads";
import { useOpenCompanyAssignmentThread } from "@/hooks/use-open-company-assignment-thread";

interface DispatchSearch {
  /** The conversation to open on arrival, when the caller came from one. */
  thread?: string;
}

export const Route = createFileRoute("/_company/company/dispatch/")({
  // The deep-link contract the new-message email nudge lands on: the board
  // opens with that conversation already in the pane. An unparseable value is
  // dropped rather than rejected — a mangled link should still reach the board.
  validateSearch: (search: Record<string, unknown>): DispatchSearch => ({
    thread: typeof search.thread === "string" && search.thread !== "" ? search.thread : undefined,
  }),
  component: DispatchPage,
});

/** How many threads the board reads to mark its rows. */
const THREAD_PAGE_SIZE = 100;

function DispatchPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { thread: openThreadId } = Route.useSearch();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const [filters, setFilters] = useState<DispatchFilterState>(emptyDispatchFilters);
  const [assignTarget, setAssignTarget] = useState<DispatchRow | null>(null);
  const { success, error: errorToast, apiError } = useToast();

  // status is the only server-side filter the company list op takes;
  // the search box filters client-side over the fetched open set.
  const params = useMemo(
    () => ({ ...(filters.status ? { status: filters.status } : {}) }),
    [filters.status],
  );
  const listQuery = useCompanyAssignments(companyId, params);
  const assignMutation = useAssignAssignment(companyId);
  const unassignMutation = useUnassignAssignment(companyId);

  // One read marks every row: the board needs to know WHICH jobs are being
  // talked about, not what was said, so it takes the thread list rather than a
  // per-row probe.
  const threadsQuery = useCompanyMessageThreads(companyId, { limit: THREAD_PAGE_SIZE });
  const unreadRows = useMemo(
    () => unreadAssignmentIds(threadsQuery.data?.items ?? []),
    [threadsQuery.data],
  );
  const openThread = useOpenCompanyAssignmentThread(companyId);
  const { mutate: openThreadFor } = openThread;
  const [openingAssignmentId, setOpeningAssignmentId] = useState<string | null>(null);

  const handleOpenMessages = useCallback(
    (row: DispatchRow) => {
      setOpeningAssignmentId(row.id);
      openThreadFor(
        { assignmentId: row.id },
        {
          // Get-or-create, so the id is known only once the server answers; the
          // pane then reads it BY ID, which is what clears the unread mark.
          onSuccess: (thread) => {
            void navigate({
              to: "/company/dispatch",
              search: { thread: thread.id },
              replace: true,
            });
          },
          onError: (err) => apiError(err.code),
          onSettled: () => setOpeningAssignmentId(null),
        },
      );
    },
    [openThreadFor, navigate, apiError],
  );

  const messagesColumn = useMemo(
    () => ({
      unreadAssignmentIds: unreadRows,
      openingAssignmentId,
      onOpen: handleOpenMessages,
    }),
    [unreadRows, openingAssignmentId, handleOpenMessages],
  );

  const rows = useMemo<DispatchRow[]>(
    () =>
      // Normalize assigned_to undefined → null at the boundary so the
      // DispatchRow type (assigned_to: string | null) and the runtime agree.
      ((listQuery.data?.items as DispatchRow[] | undefined) ?? []).map((r) => ({
        ...r,
        assigned_to: r.assigned_to ?? null,
      })),
    [listQuery.data?.items],
  );

  const grouped = useMemo(
    () => groupAssignments(filterAssignments(rows, filters.q)),
    [rows, filters.q],
  );

  const handleUnassign = (row: DispatchRow) => {
    unassignMutation.mutate(row.id, {
      onSuccess: () => success("company:dispatch.unassignSuccess"),
      // Unlike the assign mutation below, this one rethrows the wire
      // ErrorResponse body verbatim; its `code` is optional, so naming that one
      // OPTIONAL key makes the read total over whatever arrives.
      onError: (err) => apiError((err as { code?: string }).code),
    });
  };

  // Assign / Reassign / Return-to-pool are admin-only verbs — a member reads
  // the same board without the action column.
  const poolColumns = useMemo(
    () =>
      buildDispatchColumns(
        t,
        isAdmin ? (row) => setAssignTarget(row) : null,
        "assign",
        undefined,
        messagesColumn,
      ),
    [t, isAdmin, messagesColumn],
  );
  const testerColumns = useMemo(
    () =>
      buildDispatchColumns(
        t,
        isAdmin ? (row) => setAssignTarget(row) : null,
        "reassign",
        handleUnassign,
        messagesColumn,
      ),
    // handleUnassign closes over the stable mutation; rebuilding on `t` is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isAdmin, messagesColumn],
  );

  const handleAssign = (profileId: string) => {
    if (!assignTarget) return;
    assignMutation.mutate(
      { assignmentId: assignTarget.id, profileId },
      {
        onSuccess: () => {
          success("company:dispatch.assignSuccess");
          setAssignTarget(null);
        },
        // Both casts below read the `code` the mutation decorates its Error
        // with; the OPTIONAL key makes any other throwable read undefined.
        onError: (err) => {
          if ((err as { code?: string }).code === "assignment_not_dispatchable") {
            // A tester claimed it mid-dialog (or the row went terminal) —
            // close the dialog; onSettled refetches the new truth.
            errorToast("company:dispatch.notDispatchable");
            setAssignTarget(null);
            return;
          }
          apiError((err as { code?: string }).code);
        },
      },
    );
  };

  if (!active) return null;

  const hasAnyWork = grouped.pool.length > 0 || grouped.byTester.length > 0;

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("company:dispatch.title")}
        subtitle={t("company:dispatch.subtitle", { company: active.name })}
      />
      <DispatchFilterBar value={filters} onChange={setFilters} />

      {openThreadId && (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-on-surface">
              {t("company:dispatch.conversation")}
            </h2>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigate({ to: "/company/dispatch", search: {}, replace: true });
              }}
            >
              {t("company:dispatch.closeConversation")}
            </Button>
          </div>
          <RosterThreadPane key={openThreadId} companyId={companyId} threadId={openThreadId} />
        </section>
      )}

      {listQuery.isLoading ? (
        <SkeletonTable columns={7} />
      ) : !hasAnyWork ? (
        <EmptyState title={t("company:dispatch.poolEmpty")} />
      ) : (
        <div className="space-y-8">
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-on-surface">
                {t("company:dispatch.pool")}
              </h2>
              <Badge variant="default">
                {t("company:dispatch.dueChip", { count: grouped.pool.length })}
              </Badge>
            </div>
            {grouped.pool.length === 0 ? (
              <EmptyState title={t("company:dispatch.poolEmpty")} />
            ) : (
              <DataTable columns={poolColumns} data={grouped.pool} manualSorting={false} />
            )}
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-on-surface">{t("company:dispatch.board")}</h2>
            {grouped.byTester.length === 0 ? (
              <EmptyState title={t("company:dispatch.boardEmpty")} />
            ) : (
              <div className="space-y-6">
                {grouped.byTester.map((group) => (
                  <div key={group.profileId} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-on-surface">{group.name}</h3>
                      <Badge variant="default">
                        {t("company:dispatch.dueChip", {
                          count: group.rows.length,
                        })}
                      </Badge>
                    </div>
                    <DataTable columns={testerColumns} data={group.rows} manualSorting={false} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {assignTarget && (
        <AssignDialog
          companyId={companyId}
          row={assignTarget}
          onConfirm={handleAssign}
          onClose={() => setAssignTarget(null)}
          isPending={assignMutation.isPending}
        />
      )}
    </div>
  );
}
